/**
 * API client + SWR fetcher. Token from auth store.
 */
import type { AppearancePreferences } from "@/lib/theme";
import type { ImageSizeRules } from "./imageSize.ts";
import { withRequestTimeout } from "./requestTimeout.ts";

export const API_URL = import.meta.env.VITE_API_URL || "";

interface AuthSessionBridge {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  replaceSession: (session: TokenResponse) => void;
  clearSession: () => void;
}

let authSession: AuthSessionBridge = {
  getAccessToken: () => null,
  getRefreshToken: () => null,
  replaceSession: () => undefined,
  clearSession: () => undefined,
};

/**
 * Connect the framework-agnostic API client to the auth store without creating
 * a circular module dependency. The store remains the single session source.
 */
export function configureAuthSession(bridge: AuthSessionBridge): void {
  authSession = bridge;
}

export class ApiError extends Error {
  code: string;
  details: Record<string, unknown>;
  status: number;

  constructor(code: string, message: string, details: Record<string, unknown>, status: number) {
    super(message);
    this.code = code;
    this.details = details;
    this.status = status;
  }
}

/**
 * Human-readable text for a failed request. Adapter validation returns the
 * blocking reasons in `details.errors`; without them the user only sees
 * "Invalid params" and cannot tell which parameter is wrong.
 */
export function errorMessage(cause: unknown, fallback: string): string {
  if (!(cause instanceof ApiError)) return fallback;
  const reasons = cause.details.errors;
  if (Array.isArray(reasons)) {
    const listed = reasons.filter((reason): reason is string => typeof reason === "string");
    if (listed.length > 0) return `${cause.message}: ${listed.join(", ")}`;
  }
  if (typeof cause.details.mention === "string") return `${cause.message} (${cause.details.mention.startsWith("@") ? "" : "@"}${cause.details.mention})`;
  return cause.message;
}

function getToken(): string | null {
  return authSession.getAccessToken();
}

function getRefreshToken(): string | null {
  return authSession.getRefreshToken();
}

// Single in-flight refresh shared across concurrent 401s (SWR fires many).
let refreshPromise: Promise<RefreshOutcome> | null = null;

const PUBLIC_AUTH_PATHS = new Set([
  "/api/auth/bootstrap-status",
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/activate",
  "/api/auth/complete-password-change",
]);

/**
 * Outcome of a refresh attempt.
 *
 * The distinction matters: only "rejected" means the refresh token itself is no
 * good. Treating a 502 or a dropped connection the same way logged the user out
 * — losing the typed prompt, uploaded references and selected workflow — every
 * time the backend restarted.
 */
type RefreshOutcome = "refreshed" | "rejected" | "unavailable";

function refreshAccessToken(): Promise<RefreshOutcome> {
  if (refreshPromise) return refreshPromise;
  const rt = getRefreshToken();
  if (!rt) return Promise.resolve("rejected");

  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      return await withRequestTimeout(async (signal): Promise<RefreshOutcome> => {
        const res = await fetch(`${API_URL}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
          signal,
        });
        if (!res.ok) {
          // 401/403 reject the session; network/gateway failures keep it.
          return res.status === 401 || res.status === 403 ? "rejected" : "unavailable";
        }
        const data = (await res.json()) as TokenResponse;
        if (signal.aborted) return "unavailable";
        // Logging out while refresh is in flight must not resurrect the session.
        if (getRefreshToken() !== rt) return "rejected";
        if (!data.access_token || !data.refresh_token) return "unavailable";
        authSession.replaceSession(data);
        return "refreshed";
      }, 15_000);
    } catch {
      // Network failure — the server never said the token was bad.
      return "unavailable";
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function authenticatedFetch(
  path: string,
  init?: RequestInit,
  retried = false
): Promise<Response> {
  if (init?.signal?.aborted) throw init.signal.reason ?? new DOMException("Aborted", "AbortError");
  const tokenAtRequest = getToken();
  const headers = new Headers(init?.headers);
  if (!PUBLIC_AUTH_PATHS.has(path) && tokenAtRequest) {
    headers.set("Authorization", `Bearer ${tokenAtRequest}`);
  }

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (res.status !== 401 || PUBLIC_AUTH_PATHS.has(path)) return res;

  if (!retried) {
    // Another concurrent request may already have refreshed the token.
    const currentToken = getToken();
    if (currentToken && currentToken !== tokenAtRequest) {
      return authenticatedFetch(path, init, true);
    }

    if (getRefreshToken()) {
      const outcome = await refreshAccessToken();
      if (outcome === "refreshed") {
        return authenticatedFetch(path, init, true);
      }
      if (outcome === "unavailable") {
        // The refresh endpoint could not answer (backend restarting, network
        // blip). Keep the session and let the caller see the 401 — the next
        // request retries. Clearing here logged people out of a working
        // session and discarded their in-progress Studio state.
        return res;
      }
    }
  }

  // Missing refresh token, a refresh the server actively rejected, or a retried
  // request that is still unauthorized: the local session is genuinely dead.
  authSession.clearSession();
  return res;
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  // Read requests are safe to retry from the UI. Paid submissions, uploads,
  // and other mutations retain their existing behavior and are not retried.
  if (["GET", "HEAD"].includes((init?.method ?? "GET").toUpperCase())) {
    return withRequestTimeout((signal) => readApiResponse<T>(path, { ...init, signal }), 20_000, init?.signal);
  }
  return readApiResponse<T>(path, init);
}

async function readApiResponse<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  const res = await authenticatedFetch(path, { ...init, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = body?.error || {};
    throw new ApiError(err.code || "unknown", err.message || `HTTP ${res.status}`, err.details || {}, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Multipart upload of a single file to /api/assets/upload.
 * Do NOT set Content-Type by hand — the browser must add the multipart boundary.
 */
interface DirectUploadPart {
  part_number: number;
  size_bytes: number;
  url: string;
}

interface DirectUploadSession {
  id: string;
  status: "pending" | "uploaded" | "validating" | "ready" | "failed" | "aborted" | "expired";
  mode: "single" | "multipart";
  content_type: string;
  size_bytes: number;
  url?: string;
  part_size_bytes?: number;
  max_parallel_parts: number;
  parts?: DirectUploadPart[];
  error?: { code?: string; message?: string } | null;
  asset?: UploadedAsset | null;
}

function putDirect(
  url: string,
  body: Blob,
  contentType: string | null,
  onProgress: (loaded: number) => void
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (contentType) xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onerror = () => reject(new Error("Direct storage upload failed"));
    xhr.onabort = () => reject(new Error("Upload was cancelled"));
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Direct storage upload failed (HTTP ${xhr.status})`));
        return;
      }
      onProgress(body.size);
      resolve(xhr.getResponseHeader("ETag"));
    };
    xhr.send(body);
  });
}

async function abortDirectUpload(uploadId: string): Promise<void> {
  try {
    await apiFetch<void>(`/api/assets/uploads/${encodeURIComponent(uploadId)}`, {
      method: "DELETE",
    });
  } catch {
    // The expiry cleanup task remains the backstop if cancellation cannot reach the API.
  }
}

function uploadStatus(uploadId: string): Promise<DirectUploadSession> {
  return apiFetch<DirectUploadSession>(
    `/api/assets/uploads/${encodeURIComponent(uploadId)}`
  );
}

function uploadMime(file: File): string {
  if (file.type) return file.type.toLowerCase();
  const extension = file.name.split(".").pop()?.toLowerCase();
  const byExtension: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    bmp: "image/bmp",
    tif: "image/tiff",
    tiff: "image/tiff",
    gif: "image/gif",
    heic: "image/heic",
    heif: "image/heif",
    mp4: "video/mp4",
    mov: "video/quicktime",
    mp3: "audio/mpeg",
    wav: "audio/wav",
  };
  return extension ? byExtension[extension] ?? "" : "";
}

/**
 * Upload directly to R2/MinIO. Files over 100 MiB use 16 MiB multipart chunks
 * with at most three concurrent browser requests; the API only manages and
 * validates the session and never buffers the media body.
 */
export async function uploadAsset(
  file: File,
  onProgress?: (percent: number) => void
): Promise<UploadedAsset> {
  const session = await apiFetch<DirectUploadSession>("/api/assets/uploads", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      mime: uploadMime(file),
      size_bytes: file.size,
    }),
  });
  const loaded = new Map<number, number>();
  const report = () => {
    const transferred = Array.from(loaded.values()).reduce((sum, value) => sum + value, 0);
    onProgress?.(Math.min(99, Math.round((transferred / file.size) * 100)));
  };

  try {
    const completedParts: Array<{ part_number: number; etag: string }> = [];
    if (session.mode === "single") {
      if (!session.url) throw new Error("Upload URL was not returned");
      await putDirect(session.url, file, session.content_type, (value) => {
        loaded.set(1, value);
        report();
      });
    } else {
      const parts = session.parts ?? [];
      const partSize = session.part_size_bytes;
      if (!partSize || parts.length === 0) throw new Error("Multipart upload data was not returned");
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < parts.length) {
          const index = nextIndex++;
          const part = parts[index];
          const start = (part.part_number - 1) * partSize;
          const blob = file.slice(start, start + part.size_bytes);
          const etag = await putDirect(part.url, blob, null, (value) => {
            loaded.set(part.part_number, value);
            report();
          });
          if (!etag) throw new Error("Storage did not expose the multipart ETag");
          completedParts.push({ part_number: part.part_number, etag });
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(session.max_parallel_parts || 3, parts.length) },
          () => worker()
        )
      );
      completedParts.sort((a, b) => a.part_number - b.part_number);
    }

    await apiFetch<DirectUploadSession>(
      `/api/assets/uploads/${encodeURIComponent(session.id)}/complete`,
      {
        method: "POST",
        body: JSON.stringify({ parts: completedParts }),
      }
    );
    onProgress?.(99);

    const deadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < deadline) {
      const current = await uploadStatus(session.id);
      if (current.status === "ready" && current.asset) {
        onProgress?.(100);
        return current.asset;
      }
      if (["failed", "aborted", "expired"].includes(current.status)) {
        throw new ApiError(
          current.error?.code || "upload_validation_failed",
          current.error?.message || "The uploaded media did not pass validation",
          current.error ?? {},
          422
        );
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }
    throw new Error("Media validation timed out");
  } catch (error) {
    await abortDirectUpload(session.id);
    throw error;
  }
}

export interface PresignResponse {
  url: string;
}

export interface PresignOptions {
  /** Attachment disposition — the browser saves the file instead of navigating to it. */
  download?: boolean;
  filename?: string;
}

/**
 * Exchange a storage key for a short-lived, signed media URL.
 *
 * Media tags (<img>/<audio>/<video>) cannot send the Bearer header, so the
 * legacy /api/assets/proxy/{key} route always 401s. This auth'd endpoint
 * validates the principal + team scope and returns a presigned (S3/MinIO)
 * URL that carries no app JWT and expires server-side (~1h).
 */
export function presignAsset(key: string, options: PresignOptions = {}): Promise<string> {
  const qs = new URLSearchParams({ key });
  if (options.download) qs.set("download", "true");
  if (options.filename) qs.set("filename", options.filename);
  return apiFetch<PresignResponse>(`/api/assets/presign?${qs.toString()}`).then((r) => r.url);
}

/**
 * Signed media URL for an asset referenced only by id, as frozen job params do.
 * Resolves the asset's thumbnail when it has one.
 */
export function presignAssetById(assetId: string): Promise<string> {
  const qs = new URLSearchParams({ asset_id: assetId });
  return apiFetch<PresignResponse>(`/api/assets/presign?${qs.toString()}`).then((r) => r.url);
}

export { buildDownloadFilename } from "./download.ts";

/** Use the same auth/refresh path as other API requests; never expose JWTs in URLs. */
export async function downloadAssetArchive(
  entries: { storageKey: string; filename: string }[],
  signal: AbortSignal
): Promise<Blob> {
  const res = await authenticatedFetch("/api/assets/download-zip", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entries: entries.map(({ storageKey, filename }) => ({ storage_key: storageKey, filename })) }),
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = body?.error || {};
    throw new ApiError(err.code || "unknown", err.message || `HTTP ${res.status}`, err.details || {}, res.status);
  }
  if (!res.headers.get("Content-Type")?.toLowerCase().startsWith("application/zip")) {
    throw new Error("Invalid ZIP response");
  }
  // A failed/truncated stream rejects here, before any browser save is started.
  const blob = await res.blob();
  if (!blob.size) throw new Error("Empty ZIP response");
  return blob;
}

export function triggerBrowserDownload(url: string, filename?: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  if (filename) anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// ---- Types ----
export interface ModelInfo {
  key: string;
  display_name: string;
  family: string;
  modalities: string[];
  provider: string;
  provider_name: string;
  description?: string;
  tier_config?: Record<string, unknown>;
  /** Supported capability keys, so a picker can offer only usable workflows. */
  capability_keys?: string[];
  compatibility?: {
    resolutions: string[];
    qualities?: string[];
    durations: string[];
    aspect_ratios: string[];
    reference_types: string[];
    audio: boolean;
    max_outputs: number;
  };
}

export interface CapabilityInfo {
  key: string;
  label: string;
  type: string;
  ui_schema: { params: ParamSchema[] };
  constraints: Record<string, unknown>;
  sort_order: number;
}

/**
 * A selectable option. `value`/`label` are required; `group` renders a section
 * heading and `meta` carries free-form tags (e.g. voice gender/accent) shown as
 * badges. Returned by /api/catalog/{source} and also usable inline in ui_schema.
 */
export interface CatalogOption {
  value: string;
  label: string;
  group?: string;
  meta?: Record<string, string>;
}

export interface CatalogResponse {
  source: string;
  cached: boolean;
  options: CatalogOption[];
}

/**
 * Fetch dynamic, account-scoped options for a `options_source` (e.g.
 * "elevenlabs_voices") from GET /api/catalog/{source}. Unknown source → 404;
 * provider error → 502. Callers fall back to the static `options` on failure.
 */
export function getCatalogOptions(source: string): Promise<CatalogResponse> {
  return apiFetch<CatalogResponse>(`/api/catalog/${encodeURIComponent(source)}`);
}

export interface ParamSchema {
  name: string;
  /** Human-facing copy supplied by the capability registry. */
  label?: string;
  description?: string;
  section?: "creative" | "output" | "advanced";
  /** Present on negative_prompt: true only when the provider has a dedicated field. */
  native_support?: boolean;
  widget: "textarea" | "text" | "select" | "upload" | "multi_upload" | "number" | "toggle" | "camera_presets" | "hidden";
  required?: boolean;
  optional?: boolean;
  /**
   * Static options: plain strings, or {value,label[,group,meta]} when the value
   * id is opaque (e.g. ElevenLabs voice ids). Kept as the fallback when an
   * `options_source` fetch fails or returns empty.
   */
  options?: (string | CatalogOption)[];
  /**
   * When set, the frontend fetches options from /api/catalog/{options_source}
   * (account-scoped, e.g. live ElevenLabs voices); static `options` is fallback.
   */
  options_source?: string;
  default?: string | boolean | number;
  /** number: numeric max · upload/multi_upload: max asset count */
  max?: number;
  /** number: numeric min */
  min?: number;
  /** number: step increment */
  step?: number;
  mime?: string[];
  /** size (GPT Image 2 / 2.5): Studio composes WIDTHxHEIGHT from a ratio and a tier under `size_rules`. */
  aspect_ratios?: string[];
  resolutions?: string[];
  size_rules?: ImageSizeRules;
  /** Seedream: documented WIDTHxHEIGHT per level and aspect ratio. */
  size_table?: Record<string, Record<string, string>>;
}

export interface UploadedAsset {
  asset_id: string;
  type: string;
  mime: string;
  storage_key: string;
  thumbnail_storage_key: string | null;
  size: number;
  metadata?: Record<string, unknown>;
}

/** Asset kind produced by a generation. Audio added in FAZ 4C (ElevenLabs). */
export type AssetType = "image" | "video" | "audio";

/**
 * Lifecycle states emitted by the backend job state machine (app/models/job.py).
 * Terminal: completed · failed · cancelled.
 */
export type JobState =
  | "queued"
  | "reserved"
  | "dispatching"
  | "provider_pending"
  | "provider_processing"
  | "ingesting"
  | "reconciliation_required"
  | "awaiting_price_approval"
  | "completed"
  | "failed"
  | "cancelled";

/**
 * Non-terminal states where a job can still be cancelled. Pre-dispatch states
 * (queued/reserved) cancel instantly with a USD-hold refund;
 * provider_pending/provider_processing abort provider-side work where the
 * adapter supports it (fal async_queue / Topaz async_upload).
 */
export const CANCELLABLE_STATES: readonly JobState[] = [
  "queued",
  "reserved",
  "awaiting_price_approval",
  "provider_pending",
  "provider_processing",
];

export const TERMINAL_STATES: readonly JobState[] = ["completed", "failed", "cancelled"];

export const PENDING_STATES: readonly JobState[] = [
  "queued",
  "reserved",
  "dispatching",
  "provider_pending",
  "provider_processing",
  "ingesting",
  "reconciliation_required",
  "awaiting_price_approval",
];

export function isTerminalState(state: string): boolean {
  return (TERMINAL_STATES as readonly string[]).includes(state);
}

export function isPendingState(state: string): boolean {
  return (PENDING_STATES as readonly string[]).includes(state);
}

export interface JobErrorInfo {
  /** Stable product-facing code used by UI and support automation. */
  code: string;
  message: string;
  /** Exact upstream code retained for diagnosis without exposing raw payloads. */
  provider_code?: string;
  category?: "content_policy" | "invalid_request" | "configuration" | "temporary" | "cancelled" | "provider" | string;
  retryable?: boolean;
  suggested_action?: string;
}

export interface JobInfo {
  id: string;
  user_id?: string | null;
  owner_name?: string | null;
  state: JobState;
  model_key: string;
  capability_key: string;
  project_id?: string | null;
  params: Record<string, unknown>;
  result: {
    assets?: Array<{
      asset_id?: string | null;
      type: AssetType;
      storage_key: string;
      /** Image thumbnail or video poster frame, when one was stored. */
      thumbnail_storage_key?: string | null;
      size: number;
      mime_type?: string;
      metadata?: Record<string, unknown>;
    }>;
    usage?: Record<string, unknown>;
    interaction_id?: string;
  };
  error: JobErrorInfo | null;
  currency: "USD";
  estimated_charge_usd: string;
  billing_status?: "settled" | "refunded" | "reconciliation_required" | null;
  settled_charge_usd: string | null;
  queued_at: string | null;
  created_at: string;
  completed_at: string | null;
  trashed_at: string | null;
  /** Team-visible heart; null when not favorited. */
  favorited_at?: string | null;
  can_manage: boolean;
}

export interface ProjectInfo {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  settings: Record<string, unknown>;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface WalletInfo {
  team_id: string;
  currency: "USD";
  balance_usd: string;
  held_usd: string;
  available_usd: string;
  total_charged_usd: string;
  member_budget: {
    monthly_limit_usd: string | null;
    per_transaction_limit_usd: string | null;
    spent_usd: string;
    held_usd: string;
    remaining_usd: string | null;
  };
  parallel_usage: number;
  parallel_limit: number;
}

export interface CostQuoteInfo {
  id: string;
  generation_count?: number;
  kind: "generation" | "generation_reapproval" | "prompt_enhancement";
  status: string;
  expires_at: string;
  model_key?: string;
  capability_key?: string;
  params?: Record<string, unknown>;
  variant?: string | null;
  project_id?: string | null;
  cost: {
    currency: "USD";
    estimated_usd: string;
    min_usd: string | null;
    max_usd: string | null;
    guaranteed_max_usd: string;
    breakdown: Record<string, unknown>;
  };
  wallet: {
    sufficient: boolean;
    currency: "USD";
    balance_usd: string;
    held_usd: string;
    available_usd: string;
    required_usd: string;
    member_budget: WalletInfo["member_budget"];
  };
  /** Guide-based prompt warnings (GPT Image 2.5); never blocks generation. */
  prompt_checks?: { code: string; message: string }[];
  warnings: string[];
}

export interface PromptCitation {
  path: string;
  title: string;
  heading: string;
  excerpt: string;
  content_hash: string;
  url: string;
  document_code: string;
  source_kind: "official_document" | "official_skill" | "repo_snapshot";
  updated_at: string | null;
  retrieved_at: string | null;
  freshness: "live" | "cached" | "snapshot";
  fallback: boolean;
}

export interface PromptRevisionInfo {
  id: string;
  model_key: string;
  capability_key: string;
  project_id: string | null;
  original_prompt: string;
  enhanced_prompt: string;
  suggestions: string[];
  warnings: string[];
  applied_guidance: string[];
  citations: PromptCitation[];
  guide_version: string | null;
  provider_model_id: string | null;
  status: "draft" | "saved" | "applied";
  usage: Record<string, unknown>;
  cost_usd: number | null;
  created_at: string | null;
}

export interface TokenResponse {
  access_token: string | null;
  refresh_token: string | null;
  token_type: string;
  user: { id: string; email: string; full_name: string | null; is_superuser: boolean };
  active_team: SessionTeam | null;
  teams: SessionTeam[];
  requires_password_change: boolean;
  password_change_token: string | null;
}

export interface SessionTeam {
  id: string;
  name: string;
  slug: string;
  role: "super_admin" | "team_admin" | "producer" | "viewer" | string;
}

export interface TeamContext extends SessionTeam {
  active: boolean;
  current: boolean;
}

export interface TeamMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role: "team_admin" | "producer" | "viewer";
  joined_at: string | null;
  monthly_limit_usd: string | null;
  per_transaction_limit_usd: string | null;
}

export interface InvitationCredentials {
  invite_url: string;
  temporary_password: string;
  expires_at: string;
}

export interface DefaultWorkflow {
  modality: "image" | "video" | "audio";
  model_key: string;
  capability_key: string;
}

export interface LastWorkflow {
  provider_key: string;
  model_key: string;
  capability_key: string;
}

export type ModelMemory = "last_used" | "pinned";

export interface UserPreferences {
  show_unsupported_negative_prompt: boolean;
  enhance_before_generation: boolean;
  favorite_model_key: string | null;
  favorite_model_keys: string[];
  model_memory: ModelMemory;
  default_workflow: DefaultWorkflow | null;
  last_workflows: Partial<Record<"image" | "video" | "audio", LastWorkflow>>;
  appearance: AppearancePreferences;
}

// ---- Admin types ----
export interface AdminStats {
  users: { total: number; active: number };
  teams: number;
  providers: { active: number };
  models: { active: number };
  jobs: { total: number; completed: number; failed: number; pending: number };
  wallet: { currency: "USD"; total_balance_usd: string; total_held_usd: string; total_charged_usd: string };
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  password_change_required: boolean;
  last_login_at: string | null;
  created_at: string;
  memberships: Array<{ team_id: string; team_name: string; role: string }>;
}

export interface AdminTeam {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  organization: string;
  member_count: number;
  wallet_balance_usd: string;
  wallet_held_usd: string;
}

export interface AdminProvider {
  id: string;
  key: string;
  name: string;
  type: string;
  auth_method: string;
  base_url: string | null;
  secret_ref: string;
  active: boolean;
  rate_limit_config: Record<string, unknown>;
  health: Record<string, unknown>;
  model_count: number;
}

export interface PricingSyncRunInfo {
  id: string;
  provider_key: string;
  trigger: "manual" | "scheduled";
  status: "running" | "succeeded" | "failed";
  source_url: string;
  source_version: string | null;
  checked_count: number;
  updated_count: number;
  unchanged_count: number;
  started_at: string;
  finished_at: string | null;
  error: string | null;
}

export interface AdminPricingStatus {
  provider_key: string;
  source_supported: boolean;
  source_url: string | null;
  latest_run: PricingSyncRunInfo | null;
  rates: Array<{
    model_key: string;
    version: string;
    status: string;
    source_type: string;
    verified_at: string | null;
    valid_until: string | null;
    source_hash: string | null;
  }>;
}

export interface PricingPolicyInfo {
  id: string;
  scope_type: "global" | "provider" | "model";
  scope_key: string | null;
  markup_percent: string;
  fixed_fee_usd: string;
  active: boolean;
  effective_from: string;
  effective_until: string | null;
}

export interface AdminModel {
  id: string;
  key: string;
  display_name: string;
  family: string;
  modalities: string[];
  active: boolean;
  provider: string;
  provider_name: string;
  provider_active: boolean;
  description: string | null;
  deprecated_since: string | null;
}

export interface AuditEntry {
  id: string;
  team_id: string | null;
  user_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  severity: string;
  created_at: string;
}

// ---- Project helpers ----
export function listProjects(includeArchived = false): Promise<ProjectInfo[]> {
  return apiFetch<ProjectInfo[]>(`/api/projects?include_archived=${includeArchived}`);
}

export function createProject(name: string, description?: string): Promise<ProjectInfo> {
  return apiFetch<ProjectInfo>("/api/projects", {
    method: "POST",
    body: JSON.stringify({ name, description }),
  });
}

export function updateProject(
  id: string,
  patch: { name?: string; description?: string; settings?: Record<string, unknown> }
): Promise<ProjectInfo> {
  return apiFetch<ProjectInfo>(`/api/projects/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function archiveProject(id: string): Promise<ProjectInfo> {
  return apiFetch<ProjectInfo>(`/api/projects/${id}/archive`, { method: "POST" });
}

export function unarchiveProject(id: string): Promise<ProjectInfo> {
  return apiFetch<ProjectInfo>(`/api/projects/${id}/unarchive`, { method: "POST" });
}

// ---- Production assets (Elements) ----
export type ProductionAssetType = "character" | "location" | "prop" | "wardrobe";

/** A reference asset (image/audio) resolved team-scoped from the assets table. */
export interface ReferenceAssetInfo {
  asset_id: string;
  type: string;
  mime: string;
  storage_key: string;
  thumbnail_storage_key: string | null;
}

export interface ProductionAssetInfo {
  id: string;
  type: ProductionAssetType;
  name: string;
  slug: string;
  type_label: string | null;
  summary: string | null;
  generation_guidance: string | null;
  project_id: string | null;
  archived: boolean;
  /** False for read-only roles; absent on older API responses. */
  can_manage?: boolean;
  created_at: string;
  updated_at: string;
  canonical_reference_ids: string[];
  audio_reference_ids: string[];
  /** Resolved (team-scoped) image references for display. May be absent until backend serializer ships. */
  canonical_references: ReferenceAssetInfo[];
  /** Resolved (team-scoped) audio references for display. */
  audio_references: ReferenceAssetInfo[];
}

export interface ProductionAssetCreatePayload {
  type: ProductionAssetType;
  name: string;
  summary?: string;
  generation_guidance?: string;
  project_id?: string;
  type_label?: string;
  canonical_reference_ids?: string[];
  audio_reference_ids?: string[];
}

export interface ProductionAssetPatch {
  name?: string;
  summary?: string;
  generation_guidance?: string;
  type_label?: string;
  canonical_reference_ids?: string[];
  audio_reference_ids?: string[];
}

/** Build the list query path — shared as the SWR key so cache/mutate stay aligned. */
export function productionAssetsPath(
  type?: ProductionAssetType,
  projectId?: string,
  includeArchived = false
): string {
  const qs = new URLSearchParams();
  if (type) qs.set("type", type);
  if (projectId) qs.set("project_id", projectId);
  if (includeArchived) qs.set("include_archived", "true");
  const suffix = qs.toString();
  return `/api/production-assets${suffix ? `?${suffix}` : ""}`;
}

export function listProductionAssets(
  type?: ProductionAssetType,
  projectId?: string,
  includeArchived = false
): Promise<ProductionAssetInfo[]> {
  return apiFetch<ProductionAssetInfo[]>(productionAssetsPath(type, projectId, includeArchived));
}

export function createProductionAsset(
  payload: ProductionAssetCreatePayload
): Promise<ProductionAssetInfo> {
  return apiFetch<ProductionAssetInfo>("/api/production-assets", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateProductionAsset(
  id: string,
  patch: ProductionAssetPatch
): Promise<ProductionAssetInfo> {
  return apiFetch<ProductionAssetInfo>(`/api/production-assets/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function archiveProductionAsset(id: string): Promise<ProductionAssetInfo> {
  return apiFetch<ProductionAssetInfo>(`/api/production-assets/${id}/archive`, { method: "POST" });
}

export function unarchiveProductionAsset(id: string): Promise<ProductionAssetInfo> {
  return apiFetch<ProductionAssetInfo>(`/api/production-assets/${id}/unarchive`, { method: "POST" });
}

// ---- AI element portraits ----

export interface ElementGenerationJob {
  generation_id: string;
  job_id: string;
  state: JobState;
}

export interface ElementGenerationStart {
  job_id: string;
  jobs: ElementGenerationJob[];
  model_key: string;
  capability_key: string;
  prompt: string;
  requested_count: number;
  images_per_job: number;
  production_asset_id: string | null;
  parent_asset_id: string | null;
  errors: Array<{ code: string; message: string; details?: Record<string, unknown> }>;
}

export interface ElementGenerationInfo {
  id: string;
  job_id: string;
  prompt: string;
  parent_asset_id: string | null;
  created_at: string | null;
  state: JobState | "unknown";
  error: JobErrorInfo | null;
  model_key: string | null;
  assets: ReferenceAssetInfo[];
}

export interface ElementGenerationPayload {
  prompt: string;
  /** Replays a double submit instead of charging for a second run. */
  idempotency_key: string;
  model_key?: string;
  element_type?: ProductionAssetType;
  production_asset_id?: string;
  parent_asset_id?: string;
  project_id?: string;
}

export function startElementGeneration(
  payload: ElementGenerationPayload
): Promise<ElementGenerationStart> {
  return apiFetch<ElementGenerationStart>("/api/production-assets/generations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** SWR key for one element's portrait history — shared so mutate() stays aligned. */
export function elementGenerationsPath(id: string): string {
  return `/api/production-assets/${id}/generations`;
}

export function attachGeneratedReference(
  id: string,
  assetId: string
): Promise<ProductionAssetInfo> {
  return apiFetch<ProductionAssetInfo>(`/api/production-assets/${id}/attach-generated`, {
    method: "POST",
    body: JSON.stringify({ asset_id: assetId }),
  });
}

// ---- Reusable media Library ----
export type LibraryScope = "system" | "team" | "personal";

export interface LibraryCategoryInfo {
  id: string;
  scope: LibraryScope;
  name: string;
  slug: string;
  description: string | null;
  archived: boolean;
  created_by: string | null;
  can_manage?: boolean;
  item_count?: number;
  created_at: string;
  updated_at: string;
}

export interface LibraryMediaInfo {
  id: string;
  role: "primary" | "reference";
  position: number;
  metadata: { width?: number; height?: number; duration_sec?: number };
  asset_id: string | null;
  media_type: AssetType;
  mime: string;
  size_bytes: number;
  sha256: string;
}

export type CameraPresetSlot = "movement" | "camera_look" | "lens" | "focal_length" | "aperture";

export type CreativeDirectionSlot =
  | CameraPresetSlot
  | "camera_body"
  | "lighting"
  | "lighting_accent"
  | "framing"
  | "shot_angle"
  | "modifier";

export interface LibraryPresetApplication {
  target_param: "camera" | "lighting" | "prompt";
  slot: CreativeDirectionSlot;
  apply_mode: "replace_slot" | "append_many" | "bundle";
  prompt_fragment: string;
  model_keys: string[];
  capability_keys: string[];
  modalities?: Array<"image" | "video">;
  catalog_version?: string;
  stable_key?: string;
  carries_aspect_ratio?: boolean;
  excludes_slots?: CreativeDirectionSlot[];
  incompatible_with?: string[];
  max_selections?: number;
  bundle_slots?: Partial<Record<CreativeDirectionSlot, string[]>>;
  featured_rank?: number;
}

export interface LibraryItemInfo {
  id: string;
  scope: LibraryScope;
  title: string;
  description: string | null;
  prompt: string;
  media_type: AssetType;
  workflow_key: string;
  recipe: Record<string, unknown>;
  application?: LibraryPresetApplication | null;
  origin: "import" | "generation" | string;
  source: string | null;
  source_external_id: string | null;
  source_url: string | null;
  source_metadata: Record<string, unknown>;
  source_job_id: string | null;
  created_by: string | null;
  can_manage: boolean;
  duplicate?: boolean;
  archived: boolean;
  media_assets: LibraryMediaInfo[];
  supported_targets: Array<"image" | "video">;
  categories: LibraryCategoryInfo[];
  created_at: string;
  updated_at: string;
}

export interface LibraryItemsResponse {
  items: LibraryItemInfo[];
  total: number;
  limit: number;
  offset: number;
}

export interface LibraryMediaUrl {
  url: string;
  poster_url: string | null;
}

export interface LibraryStudioPayload {
  item_id: string;
  title: string;
  modality: "image" | "video" | "audio" | "upscale";
  model_key: string;
  capability_key: string;
  params: Record<string, unknown>;
  warnings: string[];
  focus_param: string;
}

export interface LibraryStudioPresetItem {
  id: string;
  title: string;
  description: string | null;
  prompt: string;
  media_type: AssetType;
  thumbnail_url: string;
  application: LibraryPresetApplication;
}

export interface LibraryStudioPresetsResponse {
  target: "camera";
  model_key: string;
  capability_key: string;
  slots: Record<CameraPresetSlot, LibraryStudioPresetItem[]>;
}

export interface CreativeDirectionPresetItem {
  media_id: string | null;
  source: string | null;
  catalog_version: string;
  id: string;
  title: string;
  description: string | null;
  slot: CreativeDirectionSlot;
  stable_key: string | null;
  prompt_fragment?: string;
  target_param?: "camera" | "lighting";
  carries_aspect_ratio?: boolean;
  excludes_slots?: CreativeDirectionSlot[];
  incompatible_with?: string[];
  media_type: AssetType;
  thumbnail_url: string | null;
  featured_rank: number | null;
  categories: string[];
  application?: LibraryPresetApplication | null;
  tags?: string[];
  swatch?: string[];
  warning?: string | null;
}

export interface CreativeDirectionBundleItem {
  id: string;
  title: string;
  description: string | null;
  stable_key: string | null;
  featured_rank: number | null;
  group?: "full_recipe" | "focus_pair";
  apply_mode?: "replace_all" | "merge";
  preset_ids: Partial<Record<CreativeDirectionSlot, string[]>>;
  thumbnail_url: string | null;
  swatch?: string[];
  warning?: string | null;
}

export interface CreativeDirectionPresetsResponse {
  modality: "image" | "video";
  model_key?: string;
  capability_key?: string;
  catalog_revision: string;
  sources: Array<{ source: string; version: string }>;
  slots: Partial<Record<CreativeDirectionSlot, CreativeDirectionPresetItem[]>>;
  bundles?: CreativeDirectionBundleItem[];
  warnings?: string[];
}

export function libraryItemsPath(filters: {
  source?: string;
  difficulty?: string;
  mood?: string;
  q?: string;
  media_type?: AssetType;
  workflow_key?: string;
  scope?: "all" | LibraryScope;
  category_ids?: string[];
  mine?: boolean;
  sort?: "newest" | "oldest" | "title";
  limit?: number;
  offset?: number;
} = {}): string {
  const qs = new URLSearchParams();
  for (const key of ["source", "difficulty", "mood"] as const) {
    if (filters[key]) qs.set(key, filters[key]);
  }
  if (filters.q) qs.set("q", filters.q);
  if (filters.media_type) qs.set("media_type", filters.media_type);
  if (filters.workflow_key) qs.set("workflow_key", filters.workflow_key);
  if (filters.scope && filters.scope !== "all") qs.set("scope", filters.scope);
  for (const categoryId of filters.category_ids ?? []) qs.append("category_ids", categoryId);
  if (filters.mine) qs.set("mine", "true");
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.limit) qs.set("limit", String(filters.limit));
  if (filters.offset) qs.set("offset", String(filters.offset));
  const suffix = qs.toString();
  return `/api/library/items${suffix ? `?${suffix}` : ""}`;
}

export function listLibraryCategories(): Promise<LibraryCategoryInfo[]> {
  return apiFetch<LibraryCategoryInfo[]>("/api/library/categories");
}

export function createLibraryCategory(payload: {
  name: string;
  scope: "team" | "personal";
  description?: string;
}): Promise<LibraryCategoryInfo> {
  return apiFetch<LibraryCategoryInfo>("/api/library/categories", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateLibraryCategory(
  id: string,
  patch: { name?: string; description?: string }
): Promise<LibraryCategoryInfo> {
  return apiFetch<LibraryCategoryInfo>(`/api/library/categories/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function archiveLibraryCategory(id: string): Promise<LibraryCategoryInfo> {
  return apiFetch<LibraryCategoryInfo>(`/api/library/categories/${id}/archive`, { method: "POST" });
}

export function saveJobResultToLibrary(payload: {
  job_id: string;
  asset_id?: string | null;
  storage_key?: string;
  title?: string;
  description?: string;
  prompt?: string;
  scope: "team" | "personal";
  category_ids: string[];
}): Promise<LibraryItemInfo> {
  return apiFetch<LibraryItemInfo>("/api/library/items/from-job", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateLibraryItem(
  id: string,
  patch: { title?: string; description?: string; prompt?: string; category_ids?: string[] }
): Promise<LibraryItemInfo> {
  return apiFetch<LibraryItemInfo>(`/api/library/items/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function archiveLibraryItem(id: string): Promise<LibraryItemInfo> {
  return apiFetch<LibraryItemInfo>(`/api/library/items/${id}/archive`, { method: "POST" });
}

export interface LibraryMediaUrlOptions {
  download?: boolean;
  filename?: string;
}

export function getLibraryMediaUrl(
  id: string,
  mediaId: string,
  options: LibraryMediaUrlOptions = {}
): Promise<LibraryMediaUrl> {
  const qs = new URLSearchParams();
  qs.set("media_id", mediaId);
  if (options.download) qs.set("download", "true");
  if (options.filename) qs.set("filename", options.filename);
  const suffix = qs.toString();
  return apiFetch<LibraryMediaUrl>(`/api/library/items/${id}/media-url${suffix ? `?${suffix}` : ""}`);
}

export function getLibraryStudioPayload(id: string, target?: "image" | "video"): Promise<LibraryStudioPayload> {
  return apiFetch<LibraryStudioPayload>(`/api/library/items/${id}/studio-payload${target ? `?target=${target}` : ""}`);
}

export function libraryStudioPresetsPath(
  modelKey: string,
  capabilityKey: string,
  target: "camera" = "camera"
): string {
  const params = new URLSearchParams({
    model_key: modelKey,
    capability_key: capabilityKey,
    target,
  });
  return `/api/library/studio-presets?${params.toString()}`;
}

export function creativeDirectionPresetsPath(
  modality: "image" | "video",
  modelKey?: string,
  capabilityKey?: string
): string {
  const params = new URLSearchParams({ modality });
  if (modelKey) params.set("model_key", modelKey);
  if (capabilityKey) params.set("capability_key", capabilityKey);
  return `/api/library/creative-direction-presets?${params.toString()}`;
}

// ---- Job helpers ----
export interface JobStateResponse {
  id: string;
  state: JobState;
}

export interface BatchInfo {
  batch_id: string;
  kind: string;
  state: string;
  size: number;
  prepared_count: number;
  unprepared_count: number;
  reserved_usd: string;
  created_at: string;
  states: Record<string, number>;
  jobs: { id: string; state: JobState; caller_index: number }[];
  next_offset: number | null;
}

export function createGenerationQuote(payload: {
  model_key: string;
  capability_key: string;
  params: Record<string, unknown>;
  variant?: string;
  project_id?: string;
  generation_count?: number;
}): Promise<CostQuoteInfo> {
  return apiFetch<CostQuoteInfo>("/api/jobs/quotes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createPriceApprovalQuote(jobId: string): Promise<CostQuoteInfo> {
  return apiFetch<CostQuoteInfo>(`/api/jobs/${jobId}/price-quote`, { method: "POST" });
}

export function approveJobPrice(jobId: string, quoteId: string): Promise<{ id: string; state: string }> {
  return apiFetch(`/api/jobs/${jobId}/approve-price`, { method: "POST", body: JSON.stringify({ quote_id: quoteId }) });
}

export function submitQuotedJob(
  quoteId: string,
  idempotencyKey: string
): Promise<JobStateResponse | BatchInfo> {
  return apiFetch<JobStateResponse | BatchInfo>("/api/jobs", {
    method: "POST",
    body: JSON.stringify({ quote_id: quoteId, idempotency_key: idempotencyKey }),
  });
}

export function createPromptEnhancementQuote(payload: {
  model_key: string;
  capability_key: string;
  prompt: string;
  project_id?: string;
  studio_context?: {
    duration?: number;
    ratio?: string;
    resolution?: string;
    size?: string;
    generate_audio?: boolean;
    camera?: string;
    creative_direction?: Record<string, unknown>;
    reference_images: number;
    reference_videos: number;
    reference_audios: number;
    has_first_frame: boolean;
    has_last_frame: boolean;
    reference_asset_ids?: string[];
  };
}): Promise<CostQuoteInfo> {
  return apiFetch<CostQuoteInfo>("/api/prompts/enhancements/quotes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function runPromptEnhancement(quoteId: string): Promise<PromptRevisionInfo> {
  return apiFetch<PromptRevisionInfo>("/api/prompts/enhancements", {
    method: "POST",
    body: JSON.stringify({ quote_id: quoteId }),
  });
}

export function updatePromptRevision(
  id: string,
  patch: { status: PromptRevisionInfo["status"]; enhanced_prompt?: string }
): Promise<PromptRevisionInfo> {
  return apiFetch<PromptRevisionInfo>(`/api/prompts/enhancements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/**
 * Cancel a non-terminal job. The backend refunds the USD hold for pre-dispatch
 * jobs and aborts provider-side work where the adapter supports it; sync providers
 * already running reject with 409. Returns the resulting job state.
 */
export function cancelJob(id: string): Promise<JobStateResponse> {
  return apiFetch<JobStateResponse>(`/api/jobs/${id}/cancel`, { method: "POST" });
}

export interface JobTrashResponse {
  id: string;
  trashed?: boolean;
  purged?: boolean;
  idempotent?: boolean;
  deleted_asset_count?: number;
  preserved_asset_count?: number;
  storage_cleanup_pending?: boolean;
}

export function moveJobToTrash(id: string): Promise<JobTrashResponse> {
  return apiFetch<JobTrashResponse>(`/api/jobs/${id}/trash`, { method: "POST" });
}

// ---- Bulk move to Trash (Gallery and Studio selections) ----
export const MAX_TRASH_ITEMS = 200;

export interface BulkTrashResult {
  trashed: string[];
  unchanged: string[];
  skipped: { id: string; reason: string }[];
}

/** One request per 200 ids; the server skips active or foreign generations instead of failing. */
export async function trashJobs(ids: string[]): Promise<BulkTrashResult> {
  const total: BulkTrashResult = { trashed: [], unchanged: [], skipped: [] };
  for (let start = 0; start < ids.length; start += MAX_TRASH_ITEMS) {
    const part = await apiFetch<BulkTrashResult>("/api/jobs/trash", {
      method: "POST",
      body: JSON.stringify({ job_ids: ids.slice(start, start + MAX_TRASH_ITEMS) }),
    });
    total.trashed.push(...part.trashed);
    total.unchanged.push(...part.unchanged);
    total.skipped.push(...part.skipped);
  }
  return total;
}

export function restoreJobFromTrash(id: string): Promise<JobTrashResponse> {
  return apiFetch<JobTrashResponse>(`/api/jobs/${id}/restore`, { method: "POST" });
}

export function permanentlyDeleteJob(id: string): Promise<JobTrashResponse> {
  return apiFetch<JobTrashResponse>(`/api/jobs/${id}`, { method: "DELETE" });
}

export interface JobFavoriteResponse {
  id: string;
  favorited: boolean;
  favorited_at: string | null;
  idempotent?: boolean;
}

export function favoriteJob(id: string): Promise<JobFavoriteResponse> {
  return apiFetch<JobFavoriteResponse>(`/api/jobs/${id}/favorite`, { method: "POST" });
}

export function unfavoriteJob(id: string): Promise<JobFavoriteResponse> {
  return apiFetch<JobFavoriteResponse>(`/api/jobs/${id}/favorite`, { method: "DELETE" });
}

// ---- Project moves (Gallery generations and Elements) ----
export const MAX_PROJECT_MOVE_ITEMS = 200;

export interface ProjectMoveResult {
  /** The target project; null means "No project". */
  project_id: string | null;
  moved: { id: string; from_project_id: string | null }[];
  unchanged: string[];
  skipped: { id: string; reason: string }[];
}

export function moveJobs(ids: string[], projectId: string | null): Promise<ProjectMoveResult> {
  return apiFetch<ProjectMoveResult>("/api/jobs/move", {
    method: "POST",
    body: JSON.stringify({ job_ids: ids, project_id: projectId }),
  });
}

export function moveProductionAssets(ids: string[], projectId: string | null): Promise<ProjectMoveResult> {
  return apiFetch<ProjectMoveResult>("/api/production-assets/move", {
    method: "POST",
    body: JSON.stringify({ asset_ids: ids, project_id: projectId }),
  });
}

// ---- Job event streaming (SSE) ----
export interface StreamTokenResponse {
  token: string;
}

/**
 * Mint a short-lived (~2min), job-scoped token for the live event stream.
 *
 * EventSource cannot attach the Bearer header, so the stream is authed via a
 * dedicated token carried in the query string of GET /api/jobs/{id}/events.
 */
export function getStreamToken(jobId: string): Promise<StreamTokenResponse> {
  return apiFetch<StreamTokenResponse>(`/api/jobs/${jobId}/stream-token`, {
    method: "POST",
  });
}

/** Build the SSE endpoint URL for a job's event stream (token is the auth). */
export function jobEventsUrl(jobId: string, token: string): string {
  return `${API_URL}/api/jobs/${jobId}/events?token=${encodeURIComponent(token)}`;
}

/**
 * Events relayed verbatim from the Redis channel `job:stream:{job_id}` over SSE.
 * `completed`/`failed` are always emitted last — on either the client refetches
 * GET /api/jobs/{id} for the final result and closes the stream.
 */
export type StreamEvent =
  | {
      type: "progress";
      state?: "provider_pending" | "provider_processing";
      pct?: number;
      message?: string;
    }
  | { type: "partial"; index: number; b64: string; mime: string }
  | { type: "completed" }
  // The server always sends an object here (every emitter builds
  // {"code", "message"}); typing it as a string meant any component that
  // rendered it would throw "Objects are not valid as a React child".
  | { type: "failed"; error: JobErrorInfo | string };

// ---- SWR fetcher ----
/**
 * Normalize a capability's ui_schema.
 *
 * The client type promises `ui_schema.params` is always an array, but the
 * server sends `c.ui_schema or {}` from a nullable column — so `.params.filter`
 * threw during render and (with no ErrorBoundary) blanked the whole app.
 */
function normalizeCapability<T>(value: T): T {
  const cap = value as unknown as { ui_schema?: { params?: unknown } };
  if (cap && typeof cap === "object" && "ui_schema" in cap) {
    const schema = cap.ui_schema;
    if (!schema || !Array.isArray(schema.params)) {
      cap.ui_schema = { ...(schema ?? {}), params: [] } as { params: never[] };
    }
  }
  return value;
}

export const swrFetcher = async <T>(path: string): Promise<T> => {
  const data = await apiFetch<T>(path);
  // Capability payloads arrive from several routes (list, per-model, presets);
  // normalizing centrally keeps every consumer safe.
  if (Array.isArray(data)) {
    data.forEach(normalizeCapability);
  } else {
    normalizeCapability(data);
  }
  return data;
};
